/**
 * +quests: Mossvale's side quests (docs/design/zones.md "Side quests"): 29 short, silly jobs from
 * 18 locals along the 1–10 journey. Mostly "kill N of the area's mobs", some "collect N things they
 * drop" (drops only while the quest is active), two "go and talk to someone" gags and three short
 * chains with a payoff joke (Hobb's socks, Wendel's feud with Clive, Kip's bandit career).
 *
 * XP sizing: a quest pays about half the XP of the kills it asks for (≈ 3.7k for all 29, of which
 * ≈ 2.9k lands before level 10 on the usual route; the main quest ≈ 1.7k, one pass of the board ≈
 * 5.5k), so questing is an alternative to plain grinding, not an XP explosion. Outgrown quests
 * (hero 3+ levels above) pay less: progression `sideQuestXp`. Totals and levelling time:
 * docs/design/zones.md "Side quests: balance".
 */
import type { NpcId, Vec2 } from '@keystone/sim';
import { fieldAreaAt } from '@keystone/sim';
import { mossvale } from './fields';
import { MAP, MOB } from './ids';
import { QUEST_NPC } from './mossvaleQuestNpcs';
import type { SideQuestDef } from './questTypes';

const M = MAP.mossvale;

/**
 * The hunting ground the tracker arrow points at: the spawn point of one of `npcIds` inside
 * `areaId` (that area or any sub-area of that region) nearest the giver, nudged toward its two
 * nearest neighbours of the same family. Read from the built zone, so it follows spawn edits.
 */
function huntAt(npcIds: readonly NpcId[], areaId: string, giverId: string): Vec2 | undefined {
  const f = mossvale.field!;
  const giver = f.npcs?.find((n) => n.id === giverId)?.pos;
  const inArea = (p: Vec2): boolean => {
    const a = fieldAreaAt(f, p);
    return !!a && (a.id === areaId || a.region === areaId);
  };
  const pts = f.spawns.filter((s) => s.npcIds.some((id) => npcIds.includes(id)) && !s.rare && inArea(s.pos)).map((s) => s.pos);
  if (!pts.length || !giver) return undefined;
  const d = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y);
  const first = pts.reduce((b, p) => (d(p, giver) < d(b, giver) ? p : b));
  const near = [...pts].sort((a, b) => d(a, first) - d(b, first)).slice(0, 3);
  return { x: Math.round((near.reduce((s, p) => s + p.x, 0) / near.length) * 10) / 10, y: Math.round((near.reduce((s, p) => s + p.y, 0) / near.length) * 10) / 10 };
}

type Def = Omit<SideQuestDef, 'mapId' | 'huntAt'>;
const q = (d: Def): SideQuestDef => {
  const spot = d.kind === 'talk' ? undefined : huntAt(d.npcIds, d.areaId, d.giverId);
  return { mapId: M, ...d, ...(spot ? { huntAt: spot } : {}) };
};

const SLIMES = [MOB.meadowSlime, MOB.bloomSlime];
const BANDITS = [MOB.briarBandit, MOB.banditSlinger];
const RISE = [MOB.paleSentinel, MOB.hollowShade];

export const MOSSVALE_SIDE_QUESTS: readonly SideQuestDef[] = [
  // ---- The Slime Hollows (1–2) ------------------------------------------------------------
  // Chain: Farmer Hobb's missing socks → the evidence → Granny Pell (payoff).
  q({
    id: 'sq_socks_1',
    giverId: QUEST_NPC.hobb,
    title: 'The Sock Situation',
    level: 1,
    minLevel: 1,
    kind: 'kill',
    npcIds: [MOB.meadowSlime],
    count: 8,
    areaId: 'slime_hollows',
    intro: [
      'Every night I hang my socks out to dry. Every morning, one is gone.',
      'Only ONE. Never the pair! That’s not a thief. That’s a monster.',
      'It’s the slimes. Look at them. Look at their smug little wobbles.',
    ],
    objective: 'Pop 8 Meadow Slimes and check them for socks.',
    progress: 'Any socks yet? Check under the wobbly bits.',
    turnIn: ['No socks?! Then where ARE they?', '...Unless they’re hiding them somewhere else. Clever jelly.'],
    reward: { xp: 60, gold: 8 },
  }),
  q({
    id: 'sq_socks_2',
    giverId: QUEST_NPC.hobb,
    title: 'Sock Evidence',
    level: 2,
    minLevel: 1,
    requires: 'sq_socks_1',
    kind: 'collect',
    npcIds: SLIMES,
    count: 5,
    item: { name: 'Damp Sock', plural: 'Damp Socks', chance: 0.6 },
    areaId: 'slime_hollows',
    intro: [
      'I had a thought. A terrible thought.',
      'What if the slimes don’t EAT the socks? What if they WEAR them?',
      'Squish a few more. If a sock pops out, bring it here. That’s evidence.',
    ],
    objective: 'Collect 5 Damp Socks from slimes in the Slime Hollows.',
    progress: 'Bring me socks. Evidence socks.',
    turnIn: ['Socks! Five of them! I KNEW it!', 'Wait. These aren’t mine. These are hand-knitted. With little hearts on.'],
    reward: { xp: 75, gold: 10 },
  }),
  q({
    id: 'sq_socks_3',
    giverId: QUEST_NPC.hobb,
    turnInId: QUEST_NPC.pell,
    title: 'Return to Sender',
    level: 2,
    minLevel: 1,
    requires: 'sq_socks_2',
    kind: 'talk',
    npcIds: [],
    count: 1,
    areaId: 'slime_hollows',
    intro: ['Little knitted hearts. Only one person in the Hollows knits hearts.', 'Granny Pell, in her cottage at the southern edge of the Hollows. Take her these socks and ask her what’s going on.'],
    objective: 'Bring the Damp Socks to Granny Pell at her cottage on the southern edge of the Hollows.',
    progress: 'Granny Pell. Southern edge, the cottage with the washing line. Go, go!',
    turnIn: ['Oh! My socks! I knitted those for the slimes, dearie. They looked cold.', 'Hobb’s socks? Oh no. His dog takes those. There’s a whole pile behind the barn.'],
    reward: { xp: 40, gold: 10 },
  }),
  q({
    id: 'sq_pip',
    giverId: QUEST_NPC.pip,
    title: 'Jelly Science',
    level: 2,
    minLevel: 1,
    kind: 'kill',
    npcIds: [MOB.bloomSlime],
    count: 6,
    areaId: 'slime_hollows',
    intro: [
      'The pink ones are Bloom Slimes. They sneeze pollen. I think they’re allergic to themselves.',
      'I need data! Squish 6 of them and remember exactly what happens.',
      'I’d do it myself, but I’m a scholar. Scholars watch.',
    ],
    objective: 'Squish 6 Bloom Slimes in the Slime Hollows. For science.',
    progress: 'Science is waiting. Science is very patient. Science is me.',
    turnIn: ['They went "splorp"? All six? Consistent results!', 'I’m calling it the Splorp Principle. You can be in the footnotes.'],
    reward: { xp: 55, gold: 8 },
  }),

  // ---- Brookside Farm and the Boar Run (2–3) ------------------------------------------------
  q({
    id: 'sq_crows_1',
    giverId: QUEST_NPC.stuffins,
    title: 'Scare the Crows',
    level: 3,
    minLevel: 2,
    kind: 'kill',
    npcIds: [MOB.tuskBoar],
    count: 8,
    areaId: 'boar_run',
    intro: [
      'Forty years I stood in that field. Not one crow was scared. Not ONE.',
      'So I retired. But I’ve found some new crows down on the Boar Run. Slow ones. Round ones.',
      'Go and scare 8 of them. Scare them very hard. With your weapon.',
    ],
    objective: 'Scare 8 "crows" (Tusk Boars) on the Boar Run.',
    progress: 'Are the crows scared yet? Be honest. Crows can tell.',
    turnIn: ['Magnificent! Those crows will think twice before crowing again.', 'Did they fly away? No? They just fell over? Even better.'],
    reward: { xp: 90, gold: 12 },
  }),
  q({
    id: 'sq_crows_2',
    giverId: QUEST_NPC.stuffins,
    title: 'Crow Feathers',
    level: 3,
    minLevel: 2,
    requires: 'sq_crows_1',
    kind: 'collect',
    npcIds: [MOB.tuskBoar],
    count: 5,
    item: { name: 'Boar Bristle', plural: 'Boar Bristles', chance: 0.6 },
    areaId: 'boar_run',
    intro: ['Every great scarecrow keeps a trophy. I’ve never had one.', 'Bring me some crow feathers. The crows down there have very stiff, bristly feathers.', 'Don’t argue. I know birds.'],
    objective: 'Collect 5 "crow feathers" (Boar Bristles) from Tusk Boars.',
    progress: 'Feathers! I need feathers! Bristly ones!',
    turnIn: ['Finest crow feathers I’ve ever seen. Very bristly crows round here.', 'Here, take this. I found it in a field. Fields have everything.'],
    reward: { xp: 90, gold: 12, gear: true },
  }),
  q({
    id: 'sq_gerald',
    giverId: QUEST_NPC.bessa,
    title: 'Justice for Gerald',
    level: 3,
    minLevel: 2,
    kind: 'kill',
    npcIds: [MOB.bloomSlime],
    count: 6,
    areaId: 'sunmeadow',
    intro: [
      'Last night the Bloom Slimes got into my garden. They ate Gerald.',
      'Gerald was my finest turnip. He was going to be a soup. A GREAT soup.',
      'I want justice. Squish 6 Bloom Slimes around the farm.',
    ],
    objective: 'Avenge Gerald: squish 6 Bloom Slimes around Brookside Farm.',
    progress: 'Gerald is watching. From turnip heaven.',
    turnIn: ['Gerald can rest now. In my memory. And partly in a slime.', 'I’ve planted a new one. I’ve called him Gerald Two. No pressure, Gerald Two.'],
    reward: { xp: 70, gold: 10 },
  }),

  // ---- Barrowfield (3–4) ------------------------------------------------------------------
  q({
    id: 'sq_graves',
    giverId: QUEST_NPC.moll,
    title: 'Grave Concerns',
    level: 4,
    minLevel: 3,
    kind: 'kill',
    npcIds: [MOB.tuskBoar],
    count: 8,
    areaId: 'barrowfield',
    intro: ['The boars in Barrowfield have found truffles under my graves.', 'Every morning: holes. Every evening: me, filling holes.', 'Chase off 8 boars so I can have one quiet evening. Just one.'],
    objective: 'Chase 8 Tusk Boars off Barrowfield’s graves.',
    progress: 'Still holes. Still filling.',
    turnIn: ['Lovely. I’m going to sit down and stare at a hole I didn’t have to fill.', 'Best evening of my life. Don’t tell my husband.'],
    reward: { xp: 100, gold: 15 },
  }),
  // Chain: Brother Fennick wants to meet a ghost → ask Moll (payoff).
  q({
    id: 'sq_ghost_1',
    giverId: QUEST_NPC.fennick,
    title: 'Spooky Business',
    level: 4,
    minLevel: 3,
    kind: 'kill',
    npcIds: [MOB.bloomSlime],
    count: 6,
    areaId: 'barrowfield',
    intro: [
      'The barrow is haunted. Everyone says so. So why won’t a ghost talk to me?',
      'I think the Bloom Slimes scare them off. All that sneezing. Very unspooky.',
      'Clear 6 of them so the ghosts feel welcome.',
    ],
    objective: 'Squish 6 Bloom Slimes in Barrowfield so the ghosts feel welcome.',
    progress: 'Too sneezy still. Ghosts hate sneezing.',
    turnIn: ['Perfect. It’s very quiet now. Very ghosty.', 'Now we wait. Ghosts love waiting.'],
    reward: { xp: 80, gold: 12 },
  }),
  q({
    id: 'sq_ghost_2',
    giverId: QUEST_NPC.fennick,
    turnInId: QUEST_NPC.moll,
    title: 'Ask the Gravedigger',
    level: 4,
    minLevel: 3,
    requires: 'sq_ghost_1',
    kind: 'talk',
    npcIds: [],
    count: 1,
    areaId: 'barrowfield',
    intro: ['Still no ghost. But Moll the gravedigger is out here every night.', 'If anyone has seen a ghost, it’s her. Go and ask her. Nicely. Ghosts are sensitive.'],
    objective: 'Ask Moll the gravedigger about the ghost.',
    progress: 'Moll is by the waystone. Ask her! Ask her!',
    turnIn: ['A ghost? Pale thing, walks about at midnight, goes "oooo"?', 'That’s Fennick. He sleepwalks in his bedsheet. Don’t tell him. It’s the happiest he’s ever been.'],
    reward: { xp: 50, gold: 10 },
  }),

  // ---- Glimmermere and the Lakeside Outpost (4–5) -----------------------------------------
  // Chain: Wendel Sprat vs Clive the snapper (payoff: Clive waves from Wisp Isle).
  q({
    id: 'sq_clive_1',
    giverId: QUEST_NPC.wendel,
    title: 'Not Clive',
    level: 5,
    minLevel: 4,
    kind: 'kill',
    npcIds: [MOB.reedbackSnapper],
    count: 8,
    areaId: 'glimmermere',
    intro: [
      'There’s a snapper in this lake called Clive. We’ve been at war for nine years.',
      'He steals my bait. He snaps my lines. Once he stole my hat. I saw him wearing it.',
      'Go and fight 8 Reedback Snappers. One of them is bound to be Clive.',
    ],
    objective: 'Defeat 8 Reedback Snappers. One of them might be Clive.',
    progress: 'Was it Clive? You’d know. He smirks.',
    turnIn: ['None of them were Clive? Are you SURE?', 'He has a chip in his shell shaped like a smirk. You’d know.'],
    reward: { xp: 120, gold: 18 },
  }),
  q({
    id: 'sq_clive_2',
    giverId: QUEST_NPC.wendel,
    title: 'Clive’s Associates',
    level: 5,
    minLevel: 4,
    requires: 'sq_clive_1',
    kind: 'collect',
    npcIds: [MOB.reedbackSnapper],
    count: 5,
    item: { name: 'Chipped Shell Bit', plural: 'Chipped Shell Bits', chance: 0.6 },
    areaId: 'glimmermere',
    intro: [
      'Clive doesn’t work alone. Nobody is that good at stealing hats alone.',
      'His gang chips their shells to look like him. Bring me 5 chipped shell bits. Evidence for the trial.',
      'Yes, there will be a trial. I’m the judge.',
    ],
    objective: 'Collect 5 Chipped Shell Bits from Reedback Snappers.',
    progress: 'The court needs evidence. The court is me.',
    turnIn: ['Look at these. Little smirks. Every one of them.', 'This goes all the way to the top. The top is Clive.'],
    reward: { xp: 120, gold: 18 },
  }),
  q({
    id: 'sq_clive_3',
    giverId: QUEST_NPC.wendel,
    title: 'The Lookouts',
    level: 5,
    minLevel: 4,
    requires: 'sq_clive_2',
    kind: 'kill',
    npcIds: [MOB.mireCroaker],
    count: 8,
    areaId: 'glimmermere',
    intro: [
      'Every time I get close, the croakers start up. Croak, croak: "Here comes Wendel."',
      'They’re Clive’s lookouts. Quiet 8 of them down.',
      'Then I’ll finally catch him. Today is the day.',
    ],
    objective: 'Silence 8 Mire Croakers, Clive’s lookouts.',
    progress: 'Croak, croak. I can still hear them telling on me.',
    turnIn: ['Quiet. Finally. Now I just cast my line and...', '...He’s on Wisp Isle. He’s WAVING at me. With my HAT. Take this. I need a little sit down.'],
    reward: { xp: 120, gold: 20, gear: true },
  }),
  q({
    id: 'sq_legs',
    giverId: QUEST_NPC.tilly,
    title: 'Leg Day',
    level: 5,
    minLevel: 4,
    kind: 'collect',
    npcIds: [MOB.mireCroaker],
    count: 6,
    item: { name: 'Croaker Leg', plural: 'Croaker Legs', chance: 0.65 },
    areaId: 'glimmermere',
    intro: [
      'The outpost wants croaker-leg soup. I want to not think about croaker legs.',
      'Could you bring me 6 of them? Don’t tell me how. Don’t tell me ANYTHING.',
      'Just wrap them in something. Something you can’t see through.',
    ],
    objective: 'Collect 6 Croaker Legs from Mire Croakers. Do not tell Tilly how.',
    progress: 'Are they wrapped? Wrap them twice.',
    turnIn: ['Don’t open it! I’ll just tip it into the pot with my eyes shut.', 'Lovely. Leg-free soup it is. These are just for decoration.'],
    reward: { xp: 130, gold: 18 },
  }),
  q({
    id: 'sq_huggable',
    giverId: QUEST_NPC.percy,
    title: 'Friendly and Huggable',
    level: 4,
    minLevel: 4,
    kind: 'kill',
    npcIds: [MOB.reedbackSnapper],
    count: 6,
    areaId: 'glimmermere',
    intro: ['I tried to hug a snapper, as the guidebook recommends. It did not go well.', 'I would like the shore to be slightly less huggable. About 6 snappers less.', 'I’ll be here. Holding my hand very carefully.'],
    objective: 'Make the shore less huggable: defeat 6 Reedback Snappers.',
    progress: 'Still very huggable out there. Too huggable.',
    turnIn: ['Splendid. I’ve crossed out "huggable" and written "absolutely not".', 'Two stars. One for the view, one for you.'],
    reward: { xp: 80, gold: 12 },
  }),

  // ---- Greyhorn Heights and Tallowdeep Mine (5–7) -----------------------------------------
  q({
    id: 'sq_offkey_1',
    giverId: QUEST_NPC.hesk,
    title: 'Off-Key on Purpose',
    level: 7,
    minLevel: 6,
    kind: 'kill',
    npcIds: [MOB.tallowChanter],
    count: 8,
    areaId: 'greyhorn',
    intro: [
      'Every day the Tallow Chanters sing down in Tallowdeep. Every day it gets worse.',
      'I’ve heard bad singers. These are bad on PURPOSE. They’re doing it to annoy me.',
      'Silence 8 of them. For music.',
    ],
    objective: 'Silence 8 Tallow Chanters around Tallowdeep Mine.',
    progress: 'Hear that? That’s a B-flat being murdered.',
    turnIn: ['Listen... silence. Beautiful silence.', '...No, wait. Now the ECHO is off-key. How is the echo off-key?'],
    reward: { xp: 150, gold: 22 },
  }),
  q({
    id: 'sq_offkey_2',
    giverId: QUEST_NPC.hesk,
    title: 'Exhibit B',
    level: 7,
    minLevel: 6,
    requires: 'sq_offkey_1',
    kind: 'collect',
    npcIds: [MOB.tallowChanter],
    count: 5,
    item: { name: 'Smudged Hymn Sheet', plural: 'Smudged Hymn Sheets', chance: 0.6 },
    areaId: 'greyhorn',
    intro: ['Nobody believes me. They say "Hesk, they’re just bad." JUST bad!', 'Bring me their songbooks. 5 hymn sheets. I’ll prove it.'],
    objective: 'Collect 5 Smudged Hymn Sheets from Tallow Chanters.',
    progress: 'The proof is in the sheet music. Get me the sheet music.',
    turnIn: ['Look! It just says "LA" four hundred times. And here: "sing this bit WORSE".', 'I KNEW it. I’m framing this one.'],
    reward: { xp: 150, gold: 22 },
  }),
  q({
    id: 'sq_hounds',
    giverId: QUEST_NPC.ottilie,
    title: 'Hound Hush',
    level: 6,
    minLevel: 5,
    kind: 'kill',
    npcIds: [MOB.cragHound],
    count: 8,
    areaId: 'greyhorn',
    intro: [
      'The Crag Hounds keep chasing my goats up the cliffs.',
      'The goats don’t mind. They LOVE cliffs. It’s me who has to climb up after them.',
      'Please see off 8 hounds. My knees will thank you.',
    ],
    objective: 'See off 8 Crag Hounds on Greyhorn Heights.',
    progress: 'Margaret is up a cliff again. Hurry, please.',
    turnIn: ['Wonderful! The goats can climb in peace. Which they’ll do anyway.', 'My knees thank you. Quietly, but they do.'],
    reward: { xp: 135, gold: 20 },
  }),
  q({
    id: 'sq_bells',
    giverId: QUEST_NPC.ottilie,
    title: 'Bells for Margaret',
    level: 6,
    minLevel: 5,
    requires: 'sq_hounds',
    kind: 'collect',
    npcIds: [MOB.cragHound],
    count: 5,
    item: { name: 'Chewed Goat Bell', plural: 'Chewed Goat Bells', chance: 0.6 },
    areaId: 'greyhorn',
    intro: ['Bad news. The hounds have been eating the goats’ bells.', 'Now the goats can’t find each other. They just stand on rocks looking confused.', 'Bring back 5 bells. Chewed is fine. Goats aren’t fussy.'],
    objective: 'Collect 5 Chewed Goat Bells from Crag Hounds.',
    progress: 'The goats are still lost. On rocks. Confused.',
    turnIn: ['*ding* Hear that? That’s Margaret! Hello, Margaret!', '*ding ding ding* ...She’s wearing all five. Take this, for your trouble.'],
    reward: { xp: 135, gold: 20, gear: true },
  }),
  q({
    id: 'sq_candles',
    giverId: QUEST_NPC.wick,
    title: 'Lumpy Candles',
    level: 7,
    minLevel: 6,
    kind: 'kill',
    npcIds: [MOB.tallowDigger],
    count: 8,
    areaId: 'greyhorn',
    intro: [
      'The Tallow Diggers make candles. Terrible candles. Lumpy, smoky, a bit cheesy.',
      'They’re flooding the market. My beautiful candles can’t compete with cheap cheese.',
      'Go and stop 8 Tallow Diggers. For quality.',
    ],
    objective: 'Stop 8 Tallow Diggers making terrible candles.',
    progress: 'I can smell them from here. Cheese. Burning cheese.',
    turnIn: ['Splendid. The market is saved. Quality wins.', '...Although I lit one of theirs. The lumpiness has a certain charm. Hmm.'],
    reward: { xp: 150, gold: 22 },
  }),

  // ---- Bramblewood (7–9) ------------------------------------------------------------------
  q({
    id: 'sq_fetch_1',
    giverId: QUEST_NPC.barnaby,
    title: 'Fetch, But Hostile',
    level: 7,
    minLevel: 7,
    kind: 'kill',
    npcIds: [MOB.brambleWolf],
    count: 10,
    areaId: 'bramblewood',
    intro: [
      'The Bramble Wolves pinched all my axes. They run off and wait for me to chase them.',
      'I’m not chasing a wolf. That’s how you become a wolf’s dinner.',
      'Show 10 of them that fetch is cancelled.',
    ],
    objective: 'Cancel fetch: defeat 10 Bramble Wolves in Bramblewood.',
    progress: 'They’re still playing fetch. With MY axes.',
    turnIn: ['Fetch is cancelled! Wonderful.', 'Did you find my axes? No? They must have buried them. Of course they did.'],
    reward: { xp: 190, gold: 25 },
  }),
  q({
    id: 'sq_fetch_2',
    giverId: QUEST_NPC.barnaby,
    title: 'So Many Handles',
    level: 7,
    minLevel: 7,
    requires: 'sq_fetch_1',
    kind: 'collect',
    npcIds: [MOB.brambleWolf],
    count: 6,
    item: { name: 'Slobbery Axe Handle', plural: 'Slobbery Axe Handles', chance: 0.6 },
    areaId: 'bramblewood',
    intro: ['I’ve got a hunch. Those wolves chewed the axe heads off and kept the handles.', 'Bring me 6 axe handles. Slobbery is fine. Slobbery is expected.'],
    objective: 'Collect 6 Slobbery Axe Handles from Bramble Wolves.',
    progress: 'Handles! I need handles!',
    turnIn: ['That’s my axe handle! And my other one! And... I don’t own this many axes.', 'Oh well. Finders keepers. I’m a handle collector now.'],
    reward: { xp: 190, gold: 25 },
  }),
  // Chain: Kip Sallow becomes the main bandit (payoff: what do main bandits actually do?).
  q({
    id: 'sq_bandit_1',
    giverId: QUEST_NPC.kip,
    title: 'Hostile Takeover',
    level: 8,
    minLevel: 7,
    kind: 'kill',
    npcIds: [MOB.briarBandit],
    count: 8,
    areaId: 'bramblewood',
    intro: [
      'The Briar gang won’t let me be the main bandit. They say I’m "too polite".',
      'So here’s my plan: you beat up the other bandits. Then there’s only me. I’m the main one.',
      '8 Briar Bandits should do it. Please. And thank you. Sorry.',
    ],
    objective: 'Beat up 8 Briar Bandits so Kip can be the main bandit.',
    progress: 'How’s the takeover going? Hostile enough?',
    turnIn: ['Brilliant. That’s 8 fewer bandits who aren’t me.', 'I’m basically middle management now.'],
    reward: { xp: 170, gold: 25 },
  }),
  q({
    id: 'sq_bandit_2',
    giverId: QUEST_NPC.kip,
    title: 'Brand Recognition',
    level: 8,
    minLevel: 7,
    requires: 'sq_bandit_1',
    kind: 'collect',
    npcIds: BANDITS,
    count: 5,
    item: { name: 'Bandit Bandana', plural: 'Bandit Bandanas', chance: 0.6 },
    areaId: 'bramblewood',
    intro: [
      'A main bandit needs a look. A brand. I’m thinking: bandanas.',
      'Collect 5 bandanas from the Briar gang. I’ll stitch my face on them.',
      'It’s a very good face. People say it’s "trustworthy", which is a problem.',
    ],
    objective: 'Collect 5 Bandit Bandanas from the Briar gang.',
    progress: 'Bandanas! For the brand!',
    turnIn: ['Look! My face! On every one! Well, I drew it. It’s mostly eyebrows.', 'One more thing and I’m official.'],
    reward: { xp: 170, gold: 25 },
  }),
  q({
    id: 'sq_bandit_3',
    giverId: QUEST_NPC.kip,
    title: 'The Main Bandit',
    level: 9,
    minLevel: 8,
    requires: 'sq_bandit_2',
    kind: 'kill',
    npcIds: [MOB.banditCaptain],
    count: 1,
    areaId: 'bramblewood',
    intro: [
      'One bandit stands between me and greatness: the Captain, in the Briar Camp.',
      'He’s big. He’s mean. He shouts a lot. I can’t do any of those things.',
      'Go and win me the job, please.',
    ],
    objective: 'Defeat the Bandit Captain in the Briar Camp.',
    progress: 'The Captain is in the camp. Shouting, probably.',
    turnIn: ['I’m the main bandit! Me! Kip! MAIN BANDIT!', '...So what do main bandits actually do? Is it mostly this? Standing in a forest? Here, take this. Bandits share. I think.'],
    reward: { xp: 180, gold: 40, gear: true },
  }),

  // ---- The Pale Rise and the Tower Road (9–10) --------------------------------------------
  q({
    id: 'sq_gloomy_1',
    giverId: QUEST_NPC.waverly,
    title: 'Cheer Up, Everyone',
    level: 9,
    minLevel: 8,
    kind: 'kill',
    npcIds: [MOB.hollowShade],
    count: 8,
    areaId: 'pale_rise',
    intro: [
      'The other shades on the Rise are SO gloomy. Moan, moan, wail. Every night.',
      'I’ve tried jokes. I’ve tried a sing-along. They just float about looking tragic.',
      'Could you send 8 of them off to wherever ghosts go next? Somewhere jollier, I hope.',
    ],
    objective: 'Send 8 Hollow Shades somewhere jollier.',
    progress: 'Still moaning out there. Such a downer.',
    turnIn: ['Oh, that’s much better! You can hear the birds now.', 'Do you think they’re happier? I like to think they’re having a picnic.'],
    reward: { xp: 185, gold: 28 },
  }),
  q({
    id: 'sq_gloomy_2',
    giverId: QUEST_NPC.waverly,
    title: 'A Light for the Dark',
    level: 10,
    minLevel: 9,
    requires: 'sq_gloomy_1',
    kind: 'collect',
    npcIds: [MOB.hollowShade],
    count: 5,
    item: { name: 'Shade Wisp', plural: 'Shade Wisps', chance: 0.6 },
    areaId: 'pale_rise',
    intro: [
      'Bit embarrassing, this. I’m afraid of the dark.',
      'Yes, I know. A ghost. Afraid of the dark. My mother would be mortified. She’s also a ghost.',
      'The shades leave little glowing wisps. Bring me 5 and I’ll make a nightlight.',
    ],
    objective: 'Collect 5 Shade Wisps from Hollow Shades.',
    progress: 'It gets awfully dark up here. Awfully.',
    turnIn: ['Oh, it’s lovely. Look how it glows! Not scary at all now.', 'Take this, old thing. I’ve carried it for three hundred years and I don’t have pockets.'],
    reward: { xp: 200, gold: 30, gear: true },
  }),
  q({
    id: 'sq_artifacts',
    giverId: QUEST_NPC.agatha,
    title: 'Do Not Touch the Artifacts',
    level: 9,
    minLevel: 8,
    kind: 'kill',
    npcIds: [MOB.paleSentinel],
    count: 8,
    areaId: 'pale_rise',
    intro: [
      'The Pale Sentinels keep stomping about on my dig site. Stomp, stomp, crunch.',
      'Do you know how many priceless pebbles they’ve crushed? I do. I counted. Eleven.',
      'Please stop 8 of them. Gently. Mind the pebbles.',
    ],
    objective: 'Stop 8 Pale Sentinels stomping on the artifacts.',
    progress: 'Crunch. That was a pebble. A PRICELESS pebble.',
    turnIn: ['Thank you! Oh, look, they crumbled into lovely little fragments.', 'More artifacts! This is the best day of my career.'],
    reward: { xp: 185, gold: 28 },
  }),
  q({
    id: 'sq_form7b',
    giverId: QUEST_NPC.bartram,
    title: 'Form 7B',
    level: 10,
    minLevel: 9,
    kind: 'kill',
    npcIds: RISE,
    count: 10,
    areaId: 'pale_rise',
    intro: [
      'Before anyone climbs the Tower, they must complete form 7B: "Proof of Being Pretty Strong".',
      'Section one: defeat 10 foes on the Pale Rise. Sentinels or shades. Both is showing off.',
      'Section two is your signature. Most people manage section two.',
    ],
    objective: 'Complete form 7B: defeat 10 Pale Sentinels or Hollow Shades.',
    progress: 'Section one is still blank. Section two is also blank.',
    turnIn: ['Approved! *stamp* You are now officially Pretty Strong.', 'Please keep this form. You will never need it again, but you must keep it.'],
    reward: { xp: 250, gold: 40 },
  }),
];

const BY_ID: ReadonlyMap<string, SideQuestDef> = new Map(MOSSVALE_SIDE_QUESTS.map((x) => [x.id, x]));

/** Every side quest (all zones). */
export const SIDE_QUESTS: readonly SideQuestDef[] = MOSSVALE_SIDE_QUESTS;

/** A side quest by id. */
export function sideQuestById(id: string): SideQuestDef | undefined {
  return BY_ID.get(id);
}

/** The quests an NPC gives, in offer order. */
export function sideQuestsFrom(npcId: string): SideQuestDef[] {
  return SIDE_QUESTS.filter((x) => x.giverId === npcId);
}

/** Who takes a quest's turn-in. */
export const sideQuestTurnInId = (d: Pick<SideQuestDef, 'giverId' | 'turnInId'>): string => d.turnInId ?? d.giverId;

/**
 * The main-quest step that opens the side quests (talking to the Elder first): until then the
 * first minutes have one obvious thing to do, as with the Bounty Board.
 */
export const SIDE_QUESTS_AFTER_STEP = 'mv_main_1_elder';
